'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import TitleBar from '../layout/TitleBar';
import CatalogItemAutocomplete, { type CatalogItemOption } from '../purchasing/CatalogItemAutocomplete';
import { searchTomVendingCatalogItems } from '../purchasing/catalogSearch';
import {
  createTomVendingPurchaseOrderFromExtract,
  extractTomVendingPoDocument,
  fetchTomVendingInventorySettings,
  fetchTomVendingPoDocumentUploadUrl,
  saveTomVendingVendor,
  useTomVendingVendorsList,
  type TvPoOcrExtract,
  type TvPoOcrMatchedLine,
} from '@/hooks/useTomVendingPurchasing';
import {
  tomVendingReceiveAtLocations,
  useTomVendingLocationsList,
} from '@/hooks/useTomVendingLocation';
import { associateTomVendingAltIdentifier } from '@/hooks/useTomVendingInventory';
import { useNotification } from '@/contexts/NotificationContext';

type ReviewLine = {
  key: string;
  raw: TvPoOcrMatchedLine;
  catalogItem: CatalogItemOption | null;
  orderedQty: string;
  unitCostDollars: string;
  include: boolean;
  /** Invoice key to learn onto the catalog row when the user picks a match. */
  learnIdentifier: string | null;
};

function invoiceLearnKey(line: TvPoOcrMatchedLine): string | null {
  const sku = line.sku?.trim();
  if (sku) return sku;
  const upc = line.upc?.trim();
  if (upc && line.matchSource !== 'upc') return upc;
  return null;
}

function extFromFile(file: File): string {
  const name = file.name || '';
  const dot = name.lastIndexOf('.');
  if (dot >= 0) return name.slice(dot + 1).toLowerCase();
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  if (file.type === 'image/gif') return 'gif';
  return 'jpg';
}

function emptyReviewRaw(): TvPoOcrMatchedLine {
  return {
    upc: null,
    sku: null,
    description: null,
    qty: null,
    unitCostDollars: null,
    confidence: null,
    matchedInventoryItem: null,
    suggestedOrderedQty: 1,
    suggestedUnitCostCents: null,
    matchSource: null,
    searchCandidates: [],
    searchQueryUsed: null,
  };
}

function photoOnlyExtract(s3Key: string): TvPoOcrExtract {
  return {
    sourceImageS3Key: s3Key,
    vendorName: null,
    poNumber: `PO-${Date.now()}`,
    documentDate: null,
    matchedVendor: null,
    defaultReceiveLocationId: null,
    lines: [],
  };
}

const TomsVendingPoFromPhotoPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { data: locations = [] } = useTomVendingLocationsList();
  const { data: vendors = [], refetch: refetchVendors } = useTomVendingVendorsList('', false);

  const [busy, setBusy] = useState(false);
  const [savingVendor, setSavingVendor] = useState(false);
  const [extract, setExtract] = useState<TvPoOcrExtract | null>(null);
  const [vendorId, setVendorId] = useState<string>('');
  const [newVendorName, setNewVendorName] = useState('');
  const [receiveLocationId, setReceiveLocationId] = useState<string>('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReviewLine[]>([]);
  const [ocrWarning, setOcrWarning] = useState<string | null>(null);

  const receiveAtLocations = useMemo(
    () => tomVendingReceiveAtLocations(locations),
    [locations],
  );
  const singleWarehouse = receiveAtLocations.length === 1;

  useEffect(() => {
    let cancelled = false;
    void fetchTomVendingInventorySettings()
      .then((s) => {
        if (cancelled || receiveLocationId) return;
        const defaultLoc = receiveAtLocations.find((l) => l.id === s.defaultReceiveLocationId);
        if (defaultLoc) {
          setReceiveLocationId(String(defaultLoc.id));
          return;
        }
        const hub = receiveAtLocations[0];
        if (hub) setReceiveLocationId(String(hub.id));
      })
      .catch(() => {
        /* ignore — user can still pick location */
      });
    return () => {
      cancelled = true;
    };
  }, [receiveAtLocations, receiveLocationId]);

  const handleSaveVendor = async () => {
    const name = newVendorName.trim();
    if (!name || savingVendor) return;
    setSavingVendor(true);
    try {
      const vendor = await saveTomVendingVendor({ name, active: true });
      await refetchVendors();
      setVendorId(String(vendor.id));
      setNewVendorName('');
      showSuccess(`Vendor "${vendor.name}" saved`);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to save vendor');
    } finally {
      setSavingVendor(false);
    }
  };

  const canPhotograph = useMemo(() => {
    return !!vendorId && !!receiveLocationId && !busy;
  }, [vendorId, receiveLocationId, busy]);

  const applyExtract = (ex: TvPoOcrExtract) => {
    setExtract(ex);
    // Keep pre-selected vendor/location; OCR vendor is informational only.
    setPoNumber(ex.poNumber ?? `PO-${Date.now()}`);
    setLines(
      (ex.lines ?? []).map((line, idx) => ({
        key: `ocr-${idx}-${line.upc ?? line.sku ?? idx}`,
        raw: line,
        catalogItem: line.matchedInventoryItem
          ? {
              id: line.matchedInventoryItem.id,
              upc: line.matchedInventoryItem.upc,
              name: line.matchedInventoryItem.name,
            }
          : null,
        orderedQty: String(line.suggestedOrderedQty || 1),
        unitCostDollars:
          line.suggestedUnitCostCents != null ? (line.suggestedUnitCostCents / 100).toFixed(2) : '',
        include: true,
        learnIdentifier: invoiceLearnKey(line),
      })),
    );
  };

  const learnAltOnPick = async (line: ReviewLine, item: CatalogItemOption) => {
    const identifier = line.learnIdentifier?.trim();
    if (!identifier) return;
    try {
      const res = await associateTomVendingAltIdentifier({
        inventoryItemId: item.id,
        identifier,
      });
      if (res.status === 'associated') {
        showSuccess(`Saved “${identifier}” as an alt identifier on ${item.upc}`);
      } else if (res.status === 'slots_full') {
        showError(`Catalog item ${item.upc} already has 4 alt identifiers — link saved for this PO only`);
      } else if (res.status === 'conflict') {
        showError(
          `“${identifier}” is already on another catalog item — PO line linked without overwriting`,
        );
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Could not save alt identifier');
    }
  };

  const uploadPoFile = async (file: File): Promise<string> => {
    const ext = extFromFile(file);
    const contentType = file.type || 'image/jpeg';
    const { uploadUrl, s3Key } = await fetchTomVendingPoDocumentUploadUrl(ext, contentType);
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    if (!put.ok) {
      throw new Error(`Upload failed (${put.status})`);
    }
    return s3Key;
  };

  const handleFile = async (file: File | null, skipScan: boolean) => {
    if (!file) return;
    if (!canPhotograph) {
      showError('Save a vendor and confirm receive location before photographing the PO');
      return;
    }
    setBusy(true);
    setOcrWarning(null);
    try {
      const s3Key = await uploadPoFile(file);
      if (skipScan) {
        applyExtract(photoOnlyExtract(s3Key));
        setOcrWarning('Photo saved without scanning. Add catalog lines now or after storing the draft.');
        showSuccess('Photo uploaded — add lines if you want, then store the order');
        return;
      }
      try {
        const matched = await extractTomVendingPoDocument(s3Key);
        applyExtract(matched);
        showSuccess('Document scanned — review lines, then store the order in the cloud');
      } catch (e: unknown) {
        applyExtract(photoOnlyExtract(s3Key));
        const msg = e instanceof Error ? e.message : 'Could not read the document';
        setOcrWarning(`${msg}. The photo is saved — add lines manually or store a photo-only draft.`);
        showError(msg);
      }
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!extract) return;
    setBusy(true);
    try {
      let resolvedVendorId = parseInt(vendorId, 10);
      if (!resolvedVendorId && newVendorName.trim()) {
        const vendor = await saveTomVendingVendor({ name: newVendorName.trim(), active: true });
        resolvedVendorId = vendor.id;
        await refetchVendors();
        setVendorId(String(vendor.id));
      }
      const locId = parseInt(receiveLocationId, 10);
      if (!resolvedVendorId || !locId || !poNumber.trim()) {
        showError('Vendor, receive location, and PO number are required');
        setBusy(false);
        return;
      }
      const createLines = lines
        .filter((l) => l.include && l.catalogItem)
        .map((l) => ({
          inventoryItemId: l.catalogItem!.id,
          orderedQty: Math.max(1, parseInt(l.orderedQty, 10) || 1),
          unitCostCents: l.unitCostDollars
            ? Math.round(parseFloat(l.unitCostDollars) * 100)
            : undefined,
        }));
      // Persist any invoice IDs that were not yet learned (e.g. associate call failed earlier).
      for (const l of lines) {
        if (!l.include || !l.catalogItem || !l.learnIdentifier?.trim()) continue;
        try {
          await associateTomVendingAltIdentifier({
            inventoryItemId: l.catalogItem.id,
            identifier: l.learnIdentifier.trim(),
          });
        } catch {
          /* non-blocking — PO still stores */
        }
      }
      if (createLines.length === 0 && !extract.sourceImageS3Key) {
        showError('Include at least one line with a catalog item');
        setBusy(false);
        return;
      }
      const po = await createTomVendingPurchaseOrderFromExtract({
        vendorId: resolvedVendorId,
        receiveLocationId: locId,
        poNumber: poNumber.trim(),
        notes: notes.trim() || undefined,
        sourceImageS3Key: extract.sourceImageS3Key,
        lines: createLines,
      });
      showSuccess(
        createLines.length === 0
          ? 'Photo stored on a draft PO — add catalog lines when you know the SKUs'
          : 'Order stored in the cloud as a draft PO — submit when ready',
      );
      navigate(`/app/toms-vending/purchasing/purchase-orders/${po.id}`);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to create PO');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TitleBar
        breadcrumbs={[
          { label: 'Home', href: '/app/dashboard' },
          { label: 'Purchase orders', href: '/app/toms-vending/purchasing/purchase-orders' },
          { label: 'From photo' },
        ]}
        title="New PO from photo"
      />

      <Box sx={{ p: 2, maxWidth: 1100 }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            1. Select vendor → photograph PO → store order in cloud
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save the vendor first (it is stored immediately), then photograph the PO. AI pulls product
            name, count, and price when it can. If formats vary too much, save the photo only and add
            lines later. Unmatched UPCs must be linked to the catalog — no placeholder products.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              select
              size="small"
              label="Vendor"
              required
              value={vendorId}
              onChange={(e) => {
                setVendorId(e.target.value);
                if (e.target.value) setNewVendorName('');
              }}
              sx={{ minWidth: 220 }}
              disabled={!!extract}
            >
              <MenuItem value="">— Create new —</MenuItem>
              {vendors.map((v) => (
                <MenuItem key={v.id} value={String(v.id)}>
                  {v.name}
                </MenuItem>
              ))}
            </TextField>
            {!vendorId && (
              <>
                <TextField
                  size="small"
                  label="New vendor name"
                  required
                  value={newVendorName}
                  onChange={(e) => setNewVendorName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleSaveVendor();
                    }
                  }}
                  sx={{ minWidth: 220 }}
                  disabled={!!extract || savingVendor}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!newVendorName.trim() || savingVendor || !!extract}
                  onClick={() => void handleSaveVendor()}
                  sx={{ textTransform: 'none', alignSelf: 'center' }}
                >
                  {savingVendor ? 'Saving…' : 'Save vendor'}
                </Button>
              </>
            )}
            {singleWarehouse ? (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Receive at {receiveAtLocations[0].name ?? `Location ${receiveAtLocations[0].id}`}{' '}
                (warehouse)
              </Typography>
            ) : (
              <TextField
                select
                size="small"
                label="Receive at"
                required
                value={receiveLocationId}
                onChange={(e) => setReceiveLocationId(e.target.value)}
                sx={{ minWidth: 220 }}
                disabled={!!extract}
              >
                {receiveAtLocations.map((l) => (
                  <MenuItem key={l.id} value={String(l.id)}>
                    {l.name ?? `Location ${l.id}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="contained"
              component="label"
              disabled={!canPhotograph || !!extract}
              sx={{ textTransform: 'none' }}
            >
              {busy ? 'Working…' : '2. Photograph / upload PO'}
              <input
                hidden
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => void handleFile(e.target.files?.[0] ?? null, false)}
              />
            </Button>
            <Button
              variant="outlined"
              component="label"
              disabled={!canPhotograph || !!extract}
              sx={{ textTransform: 'none' }}
            >
              Save photo only (skip scan)
              <input
                hidden
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => void handleFile(e.target.files?.[0] ?? null, true)}
              />
            </Button>
          </Box>
          {!canPhotograph && !extract && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Save a vendor (use Save vendor) before photographing. Receive location is the warehouse.
            </Typography>
          )}
        </Paper>

        {extract && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              3. Review lines, then store in cloud
            </Typography>
            {ocrWarning && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                {ocrWarning}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                select
                size="small"
                label="Vendor"
                value={vendorId}
                onChange={(e) => {
                  setVendorId(e.target.value);
                  if (e.target.value) setNewVendorName('');
                }}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">— Create new —</MenuItem>
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={String(v.id)}>
                    {v.name}
                  </MenuItem>
                ))}
              </TextField>
              {!vendorId && (
                <>
                  <TextField
                    size="small"
                    label="New vendor name"
                    value={newVendorName}
                    onChange={(e) => setNewVendorName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleSaveVendor();
                      }
                    }}
                    sx={{ minWidth: 220 }}
                    disabled={savingVendor}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!newVendorName.trim() || savingVendor}
                    onClick={() => void handleSaveVendor()}
                    sx={{ textTransform: 'none', alignSelf: 'center' }}
                  >
                    {savingVendor ? 'Saving…' : 'Save vendor'}
                  </Button>
                </>
              )}
              {singleWarehouse ? (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Receive at {receiveAtLocations[0].name ?? `Location ${receiveAtLocations[0].id}`}{' '}
                  (warehouse)
                </Typography>
              ) : (
                <TextField
                  select
                  size="small"
                  label="Receive at"
                  value={receiveLocationId}
                  onChange={(e) => setReceiveLocationId(e.target.value)}
                  sx={{ minWidth: 220 }}
                >
                  {receiveAtLocations.map((l) => (
                    <MenuItem key={l.id} value={String(l.id)}>
                      {l.name ?? `Location ${l.id}`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                size="small"
                label="PO number"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                sx={{ minWidth: 180 }}
              />
              <TextField
                size="small"
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ minWidth: 220 }}
              />
            </Box>
            {extract.vendorName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                OCR read vendor as “{extract.vendorName}” (your selected vendor is used unless you
                change it above).
              </Typography>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>OCR</TableCell>
                  <TableCell>Catalog item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit cost ($)</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No scanned lines — add catalog items below, or store the photo-only draft.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {lines.map((line) => (
                  <TableRow
                    key={line.key}
                    sx={{ opacity: line.include ? 1 : 0.45, bgcolor: line.catalogItem ? undefined : 'action.hover' }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {line.raw.description || line.raw.sku || line.raw.upc || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        UPC {line.raw.upc || '—'}
                        {line.raw.sku ? ` · SKU ${line.raw.sku}` : ''}
                        {line.raw.confidence != null
                          ? ` · conf ${(line.raw.confidence * 100).toFixed(0)}%`
                          : ''}
                        {line.raw.matchSource === 'alt_identifier' ? ' · matched via alt ID' : ''}
                        {line.raw.matchSource === 'upc' ? ' · matched via UPC' : ''}
                      </Typography>
                      {!line.catalogItem && line.include && (
                        <Chip
                          size="small"
                          color="warning"
                          label={
                            (line.raw.searchCandidates?.length ?? 0) > 0
                              ? 'Pick from search'
                              : 'Pick catalog SKU'
                          }
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      {!line.catalogItem && line.raw.searchQueryUsed && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Search: “{line.raw.searchQueryUsed}”
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <CatalogItemAutocomplete
                        value={line.catalogItem}
                        onChange={(item) => {
                          setLines((prev) =>
                            prev.map((l) => (l.key === line.key ? { ...l, catalogItem: item } : l)),
                          );
                          if (item && !line.catalogItem) {
                            void learnAltOnPick(line, item);
                          }
                        }}
                        searchCatalog={searchTomVendingCatalogItems}
                        sx={{ minWidth: 280 }}
                        disabled={!line.include}
                        initialOptions={
                          !line.catalogItem && (line.raw.searchCandidates?.length ?? 0) > 0
                            ? (line.raw.searchCandidates ?? []).map((c) => ({
                                id: c.id,
                                upc: c.upc,
                                name: c.name,
                              }))
                            : undefined
                        }
                        initialInputValue={
                          !line.catalogItem
                            ? line.raw.searchQueryUsed || line.raw.description || undefined
                            : undefined
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={line.orderedQty}
                        disabled={!line.include}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((l) =>
                              l.key === line.key ? { ...l, orderedQty: e.target.value } : l,
                            ),
                          )
                        }
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={line.unitCostDollars}
                        disabled={!line.include}
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((l) =>
                              l.key === line.key ? { ...l, unitCostDollars: e.target.value } : l,
                            ),
                          )
                        }
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label="Exclude line"
                        onClick={() =>
                          setLines((prev) =>
                            prev.map((l) =>
                              l.key === line.key ? { ...l, include: !l.include } : l,
                            ),
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => void handleCreate()}
                sx={{ textTransform: 'none' }}
              >
                Store order in cloud (draft PO)
              </Button>
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setLines((prev) => [
                    ...prev,
                    {
                      key: `manual-${Date.now()}`,
                      raw: emptyReviewRaw(),
                      catalogItem: null,
                      orderedQty: '1',
                      unitCostDollars: '',
                      include: true,
                      learnIdentifier: null,
                    },
                  ]);
                }}
              >
                Add line
              </Button>
              <Button
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setExtract(null);
                  setLines([]);
                  setOcrWarning(null);
                }}
              >
                Retake photo
              </Button>
              <Button
                sx={{ textTransform: 'none' }}
                onClick={() => navigate('/app/toms-vending/purchasing/purchase-orders')}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );
};

export default TomsVendingPoFromPhotoPage;
